import {useState, useEffect, useCallback, useRef} from "react";
import {ApiError, ApiResponse, ErrorDetail} from "../api/types";
import {result} from "lodash";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

export interface QueryOptions<TData> {
    enabled?: boolean;
    defaultValue?: TData;
    onSuccess?: (data: TData) => void;
    onError?: (error: Error) => void;
}

export interface ResourceOptions<TData> {
    defaultValue?: TData;
    onSuccess?: (data: TData) => void;
    onError?: (error: Error) => void;
}

export interface FilteredQueryOptions<TData> {
    paged?: boolean;
    autoLoad?: boolean;
    defaultValue?: TData[];
    onSuccess?: (data: TData[]) => void;
    onError?: (error: Error) => void;
}

export interface QueryResult<TData> {
    data: TData;
    loading: boolean;
    error: Error | null;
    refetch: () => Promise<TData | undefined>;
}

export interface ResourceResult<TData> {
    data: TData;
    loading: boolean;
    error: Error | null;
    reload: () => Promise<TData | undefined>;
}

export interface ApiResourceResult<TData> {
    data: TData;
    loading: boolean;
    error: ErrorDetail | null;
    reload: () => Promise<void>;
}

export interface FilteredQueryResult<TData, TFilters> {
    data: TData[];
    loading: boolean;
    error: Error | null;
    filters: TFilters;
    currentPage: number;
    totalPages: number;
    hasMore: boolean;
    setFilters: (newFilters: Partial<TFilters>) => void;
    loadNextPage: () => void;
    reload: () => void;
    reset: () => void;
    fetch: (page?: number) => Promise<void>;
}

// ============================================================================
// BASE HOOK: useQuery
// ============================================================================

/**
 * Generic hook for async data fetching with automatic dependency tracking.
 * Perfect for queries that depend on parameters and should re-fetch when they change.
 *
 * @example
 * const { data, loading, error, refetch } = useQuery(
 *   fetchUser,
 *   [userId],
 *   { enabled: !!userId }
 * );
 */
export function useQuery<TData, TParams extends any[]>(
    fetcher: (...params: TParams) => Promise<TData>,
    params: TParams,
    options: QueryOptions<TData> = {}
): QueryResult<TData> {
    const {enabled = true, defaultValue, onSuccess, onError} = options;

    const [data, setData] = useState<TData | null>(defaultValue ?? null);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState<Error | null>(null);

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const execute = useCallback(async () => {
        if (!enabled || !isMountedRef.current) return;

        setLoading(true);
        setError(null);

        try {
            const result = await fetcher(...params);

            if (!isMountedRef.current) return;

            setData(result);
            onSuccess?.(result);
            return result;
        } catch (err) {
            if (!isMountedRef.current) return;

            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            onError?.(error);
            console.error("Query error:", err);
            throw error;
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [fetcher, enabled, onSuccess, onError, ...params]);

    useEffect(() => {
        execute();
    }, [execute]);

    const refetch = useCallback(() => execute(), [execute]);

    return {
        data: data as TData,
        loading,
        error,
        refetch
    };
}

// ============================================================================
// BASE HOOK: useResource
// ============================================================================

/**
 * Generic hook for simple resource loading (load once on mount).
 * Use for data that doesn't depend on parameters and only needs to load once.
 *
 * @example
 * const { data: branches, loading, error, reload } = useResource(
 *   loadBranches,
 *   { defaultValue: [] }
 * );
 */
export function useResource<TData>(
    fetcher: () => Promise<TData>,
    options: ResourceOptions<TData> = {}
): ResourceResult<TData> {
    const {defaultValue, onSuccess, onError} = options;
    const [data, setData] = useState<TData | null>(defaultValue ?? null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        if (!isMountedRef.current) return;

        setLoading(true);
        setError(null);

        try {
            const result = await fetcher();

            if (!isMountedRef.current) return;

            setData(result);
            onSuccess?.(result);
            return result;
        } catch (err) {
            if (!isMountedRef.current) return;

            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            onError?.(error);
            console.error("Resource loading error:", err);
            throw error;
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [fetcher, onSuccess, onError]);

    useEffect(() => {
        load();
    }, []);

    return {
        data: data as TData,
        loading,
        error,
        reload: load
    };
}



// ============================================================================
// BASE HOOK: useFilteredQuery
// ============================================================================

/**
 * Generic hook for filtered/paginated data with complex state management.
 * Perfect for lists with search, filters, and pagination.
 *
 * @example
 * const { data, loading, setFilters, loadNextPage } = useFilteredQuery(
 *   fetchEmployees,
 *   { query: "", departments: [] },
 *   { paged: true, autoLoad: true }
 * );
 */
export function useFilteredQuery<TData, TFilters extends Record<string, any>>(
    fetcher: (filters: TFilters, page?: number) => Promise<{
        data: TData[];
        totalPages?: number;
    }>,
    initialFilters: TFilters,
    options: FilteredQueryOptions<TData> = {}
): FilteredQueryResult<TData, TFilters> {
    const {
        paged = true,
        autoLoad = true,
        defaultValue = [],
        onSuccess,
        onError
    } = options;

    const [data, setData] = useState<TData[]>(defaultValue);
    const [loading, setLoading] = useState(autoLoad);
    const [filters, setFilters] = useState<TFilters>(initialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [error, setError] = useState<Error | null>(null);

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchData = useCallback(async (
        page: number,
        currentFilters: TFilters,
        append = false
    ) => {
        if (!isMountedRef.current) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetcher(currentFilters, paged ? page : undefined);

            if (!isMountedRef.current) return;

            if (append) {
                setData(prev => [...prev, ...response.data]);
            } else {
                setData(response.data);
            }

            if (paged && response.totalPages !== undefined) {
                setTotalPages(response.totalPages);
            }

            onSuccess?.(response.data);
        } catch (err) {
            if (!isMountedRef.current) return;

            const error = err instanceof Error ? err : new Error(String(err));
            setError(error);
            onError?.(error);
            console.error("Filtered query error:", err);
        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [fetcher, paged, onSuccess, onError]);

    // Initial load
    useEffect(() => {
        if (autoLoad) {
            fetchData(1, filters);
        }
    }, []);

    // Filter changes trigger reload
    useEffect(() => {
        if (autoLoad) {
            setCurrentPage(1);
            fetchData(1, filters);
        }
    }, [filters, fetchData, autoLoad]);

    const updateFilters = useCallback((newFilters: Partial<TFilters>) => {
        setFilters(prev => ({...prev, ...newFilters}));
    }, []);

    const loadNextPage = useCallback(() => {
        if (paged && currentPage < totalPages && !loading) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            fetchData(nextPage, filters, true);
        }
    }, [paged, currentPage, totalPages, loading, fetchData, filters]);

    const reload = useCallback(() => {
        setCurrentPage(1);
        fetchData(1, filters);
    }, [fetchData, filters]);

    const reset = useCallback(() => {
        setData(defaultValue);
        setCurrentPage(1);
        setTotalPages(1);
        setError(null);
        setFilters(initialFilters);
    }, [initialFilters, defaultValue]);

    const fetch = useCallback(async (page = 1) => {
        await fetchData(page, filters);
    }, [fetchData, filters]);

    return {
        data,
        loading,
        error,
        filters,
        currentPage,
        totalPages,
        hasMore: paged ? currentPage < totalPages : false,
        setFilters: updateFilters,
        loadNextPage,
        reload,
        reset,
        fetch
    };
}

// ============================================================================
// UTILITY: Callback to Promise Adapter
// ============================================================================

/**
 * Converts a callback-based API to a promise-based one.
 * Useful for legacy APIs that use callbacks.
 *
 * @example
 * const fetcher = callbackToPromise(loadBranches);
 */
export function callbackToPromise<TData>(
    loader: (callback: (response: TData) => void) => Promise<void>
): () => Promise<TData> {
    return () => new Promise<TData>((resolve, reject) => {
        loader(resolve).catch(reject);
    });
}


export function useApiResource<TData>(
    fetcher: () => Promise<ApiResponse<TData>>,
    options: ResourceOptions<TData> = {}
): ApiResourceResult<TData> {

    const [data, setData] = useState<TData>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<ErrorDetail | null>(null);

    const isMountedRef = useRef(true);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const load = useCallback(async () => {
        if (!isMountedRef.current) return;

        setLoading(true);
        setError(null);

        try {
            const response = await fetcher();

            if (!isMountedRef.current) return;

            if (response.success && response.data) {
                setData(response.data);
            }

            if (response.error) {
                setError(response.error);
            }

        } catch (err) {
            if (!isMountedRef.current) return;

            const errorMessage = err instanceof Error ? err.message : String(err);
            setError({
                code: 'UNEXPECTED_ERROR',
                message: errorMessage
            });

            console.error("Resource loading error:", err);
            throw error;

        } finally {
            if (isMountedRef.current) {
                setLoading(false);
            }
        }
    }, [fetcher]);

    useEffect(() => {
        load();
    }, []);

    return {
        data: data as TData,
        loading,
        error,
        reload: load
    };
}